import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Cookie, Shield, Settings, Eye } from 'lucide-react';

export default function CookiePolicyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#00FF41] transition-colors mb-8" data-testid="cookies-back-btn">
          <ArrowLeft className="h-4 w-4" /> Back to Dashboard
        </button>

        <h1 className="text-4xl font-bold mb-2">
          <span className="text-white">Cookie </span>
          <span className="text-[#00FF41]">Policy</span>
        </h1>
        <p className="text-gray-400 mb-8">How BitBot uses cookies and similar technologies. Last updated: February 2026.</p>

        <div className="space-y-8">
          <section>
            <h2 className="text-xl font-bold text-[#00FF41] mb-3 flex items-center gap-2">
              <Cookie className="h-5 w-5" /> What are cookies?
            </h2>
            <p className="text-gray-300 leading-relaxed">
              Cookies are small text files that a website places on your device when you visit. They let the site
              remember things between page loads and across sessions — like the fact that you're logged in, your
              theme preference, or which coins you have favorited.
            </p>
            <p className="text-gray-300 leading-relaxed mt-3">
              "Similar technologies" includes localStorage, sessionStorage, IndexedDB, and the service worker
              cache — all of which BitBot uses in normal operation. For simplicity, we'll refer to all of these
              as "cookies" throughout this page.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#00FF41] mb-3 flex items-center gap-2">
              <Shield className="h-5 w-5" /> What cookies BitBot uses
            </h2>
            <div className="space-y-4">
              <div className="border border-[#003B00] bg-[#0a0a0a] p-4">
                <div className="text-[#00FF41] font-bold text-sm mb-1">Essential cookies (required)</div>
                <p className="text-gray-300 text-sm leading-relaxed mb-2">
                  Used to authenticate you and keep you logged in. Without these, the platform cannot function.
                </p>
                <ul className="text-gray-400 text-xs space-y-1 ml-4 list-disc">
                  <li><code className="text-[#00FF41]">bitbot_session</code> — your encrypted session token (HTTP-only, secure)</li>
                  <li><code className="text-[#00FF41]">access_level</code> — cached membership tier to avoid re-fetching</li>
                  <li><code className="text-[#00FF41]">last_active_pair</code> — remembers the last coin you viewed</li>
                </ul>
              </div>
              <div className="border border-[#003B00] bg-[#0a0a0a] p-4">
                <div className="text-[#00FF41] font-bold text-sm mb-1">Preference cookies</div>
                <p className="text-gray-300 text-sm leading-relaxed mb-2">
                  Remember your settings so the app behaves consistently between visits.
                </p>
                <ul className="text-gray-400 text-xs space-y-1 ml-4 list-disc">
                  <li><code className="text-[#00FF41]">audio_alerts_enabled</code> — whether trade sounds play</li>
                  <li><code className="text-[#00FF41]">chart_timeframe</code> — your default chart range (1H, 1D, 30D, etc.)</li>
                  <li><code className="text-[#00FF41]">whats_new_dismissed</code> — which changelog cards you've closed</li>
                  <li><code className="text-[#00FF41]">music_player_state</code> — your music player preferences</li>
                </ul>
              </div>
              <div className="border border-[#003B00] bg-[#0a0a0a] p-4">
                <div className="text-[#00FF41] font-bold text-sm mb-1">Functional cookies</div>
                <p className="text-gray-300 text-sm leading-relaxed mb-2">
                  Help the PWA (Progressive Web App) work offline and keep performance snappy.
                </p>
                <ul className="text-gray-400 text-xs space-y-1 ml-4 list-disc">
                  <li><strong>Service worker cache</strong> — precached app shell + recent API responses so the app loads instantly</li>
                  <li><strong>IndexedDB</strong> — local cache of your trade history for the Stats page</li>
                </ul>
              </div>
              <div className="border border-[#003B00] bg-[#0a0a0a] p-4">
                <div className="text-[#00FF41] font-bold text-sm mb-1">Analytics & advertising cookies (third-party)</div>
                <p className="text-gray-300 text-sm leading-relaxed mb-2">
                  If you have not disabled them, the following third parties may set cookies through BitBot:
                </p>
                <ul className="text-gray-400 text-xs space-y-1 ml-4 list-disc">
                  <li><strong>Google AdSense</strong> — for the optional ads shown on public/educational pages.
                    AdSense uses cookies to serve relevant ads and measure their effectiveness.
                    <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer" className="text-[#00FF41] hover:underline ml-1">Google's ad policy</a>.
                  </li>
                  <li><strong>Google Analytics</strong> (if enabled) — anonymized usage statistics, no personal trading data is shared.</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#00FF41] mb-3 flex items-center gap-2">
              <Eye className="h-5 w-5" /> What we do NOT use cookies for
            </h2>
            <ul className="text-gray-300 leading-relaxed list-disc list-inside space-y-2 pl-2">
              <li>We do not sell your data to third parties.</li>
              <li>We do not use tracking pixels to follow you across other websites.</li>
              <li>We do not store your Coinbase API keys in any browser cookie. They live in encrypted server-side storage only.</li>
              <li>We do not use cookies to determine ad targeting based on your trade history.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#00FF41] mb-3 flex items-center gap-2">
              <Settings className="h-5 w-5" /> How to control cookies
            </h2>
            <p className="text-gray-300 leading-relaxed mb-3">
              You have full control over cookies. Note that disabling essential cookies will prevent you from
              logging in — so if you want to use BitBot, those are required.
            </p>
            <ul className="text-gray-300 leading-relaxed list-disc list-inside space-y-2 pl-2">
              <li>
                <strong>Block all cookies for BitBot</strong> — Most browsers let you block cookies on a per-site
                basis via Settings → Privacy → Site Settings → Cookies and site data. Bear in mind that this will
                log you out and prevent the app from remembering anything.
              </li>
              <li>
                <strong>Clear existing cookies</strong> — Settings → Privacy → Clear browsing data → Cookies and other
                site data. This will log you out and reset your preferences. Your account on our servers is not
                affected — only the local browser state.
              </li>
              <li>
                <strong>Opt out of personalized Google ads</strong> — visit
                {' '}<a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer" className="text-[#00FF41] hover:underline">Google Ad Settings</a>{' '}
                and turn off Ad Personalization. You'll still see ads, but they won't be tailored to your interests.
              </li>
              <li>
                <strong>Use incognito / private browsing</strong> — cookies set during a private session are deleted
                when you close the window. This is a quick way to test the site without persistent state.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#00FF41] mb-3">Do Not Track</h2>
            <p className="text-gray-300 leading-relaxed">
              Some browsers send a "Do Not Track" header. There is no universal standard for what this header means
              and major web standards bodies have abandoned it. BitBot itself does not implement cross-site tracking
              regardless of whether your browser sends DNT — but third-party services like Google AdSense may behave
              differently. Refer to their respective privacy policies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#00FF41] mb-3">Changes to this policy</h2>
            <p className="text-gray-300 leading-relaxed">
              We will update this page when we add new third-party integrations or change how cookies are used.
              The "Last updated" date at the top reflects the most recent revision. We recommend checking it
              periodically. Major changes will also be called out in the in-app changelog.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-[#00FF41] mb-3">Questions</h2>
            <p className="text-gray-300 leading-relaxed">
              Got a question or a concern about how a specific cookie is used? Drop us a line via the
              {' '}<button onClick={() => navigate('/contact')} className="text-[#00FF41] hover:underline">Contact page</button>.
            </p>
          </section>
        </div>

        <footer className="mt-12 pt-6 border-t border-[#003B00] text-xs text-gray-600 flex flex-wrap gap-4">
          <button onClick={() => navigate('/about')} className="hover:text-[#00FF41]">About</button>
          <button onClick={() => navigate('/faq')} className="hover:text-[#00FF41]">FAQ</button>
          <button onClick={() => navigate('/contact')} className="hover:text-[#00FF41]">Contact</button>
          <button onClick={() => navigate('/privacy')} className="hover:text-[#00FF41]">Privacy</button>
          <button onClick={() => navigate('/terms')} className="hover:text-[#00FF41]">Terms</button>
          <button onClick={() => navigate('/disclaimer')} className="hover:text-[#00FF41]">Disclaimer</button>
        </footer>
      </div>
    </div>
  );
}
